import { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { WebSocketProvider } from "@/contexts/WebSocketContext";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Discover from "@/pages/Discover";
import Matches from "@/pages/Matches";
import Chat from "@/pages/Chat";
import Feed from "@/pages/Feed";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import CustomizeProfile from "@/pages/CustomizeProfile";
import Notifications from "@/pages/Notifications";
import VideoFeed from "@/pages/VideoFeed";
import ArtistHub from "@/pages/ArtistHub";
import TipArtist, { TipSuccess, TipCancel } from "@/pages/TipArtist";
import LiveBroadcast from "@/pages/LiveBroadcast";
import Stories from "@/pages/Stories";
import Premium, { PremiumSuccess } from "@/pages/Premium";
import Boost, { BoostSuccess } from "@/pages/Boost";
import Promote, { PromoteSuccess } from "@/pages/Promote";
import Gifts, { GiftsSuccess } from "@/pages/Gifts";
import LiveRTC from "@/pages/LiveRTC";
import DuetRecorder from "@/pages/DuetRecorder";
import Plans, { PlansSuccess } from "@/pages/Plans";
import DistributionHub from "@/pages/DistributionHub";
import KYCGate from "@/pages/KYCGate";
import Referrals from "@/pages/Referrals";
import Contests from "@/pages/Contests";
import ContestDetail from "@/pages/ContestDetail";
import AdminContests from "@/pages/AdminContests";
import AdminReports from "@/pages/AdminReports";
import AdminAutoContest from "@/pages/AdminAutoContest";
import AIDating from "@/pages/AIDating";
import AIDatingCreate from "@/pages/AIDatingCreate";
import AIDatingChat from "@/pages/AIDatingChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Backend now returns session_token in /me response body so it's
        // reachable from JS even though the cookie is HttpOnly. Used to open WS.
        if (userData.session_token) {
          setToken(userData.session_token);
        }
      } else {
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      // Auth check failed - user stays logged out
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    if (authToken) setToken(authToken);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Logout cleanup - user still redirected
    }
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowWithoutKyc = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5E1] flex items-center justify-center">
        <div className="brutal-card animate-pulse">
          <p className="text-xl font-bold">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force KYC for any protected page that doesn't explicitly allow unverified access
  if (!allowWithoutKyc && !user.kyc_verified) {
    return <Navigate to="/kyc" replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const { token } = useAuth();

  // Check URL fragment for session_id SYNCHRONOUSLY
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <WebSocketProvider token={token}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route
          path="/kyc"
          element={
            <ProtectedRoute allowWithoutKyc={true}>
              <KYCGate />
            </ProtectedRoute>
          }
        />
        <Route path="/contests" element={<Contests />} />
        <Route path="/contests/:id" element={<ContestDetail />} />
        <Route
          path="/admin/contests"
          element={
            <ProtectedRoute allowWithoutKyc={true}>
              <AdminContests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute allowWithoutKyc={true}>
              <AdminReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/auto-contest"
          element={
            <ProtectedRoute allowWithoutKyc={true}>
              <AdminAutoContest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute allowWithoutKyc={true}>
              <Referrals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discover"
          element={
            <ProtectedRoute>
              <Discover />
            </ProtectedRoute>
          }
        />
        <Route
          path="/matches"
          element={
            <ProtectedRoute>
              <Matches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:matchId"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <Feed />
            </ProtectedRoute>
          }
        />
        <Route path="/profile/:userId" element={<Profile />} />
        <Route
          path="/edit-profile"
          element={
            <ProtectedRoute>
              <EditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customize"
          element={
            <ProtectedRoute>
              <CustomizeProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/videos"
          element={
            <ProtectedRoute>
              <VideoFeed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/music"
          element={
            <ProtectedRoute>
              <ArtistHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tip/:userId"
          element={
            <ProtectedRoute>
              <TipArtist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tip/success"
          element={
            <ProtectedRoute>
              <TipSuccess />
            </ProtectedRoute>
          }
        />
        <Route path="/tip/cancel" element={<TipCancel />} />
        <Route
          path="/live"
          element={
            <ProtectedRoute>
              <LiveBroadcast />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories"
          element={
            <ProtectedRoute>
              <Stories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/premium"
          element={
            <ProtectedRoute>
              <Premium />
            </ProtectedRoute>
          }
        />
        <Route
          path="/premium/success"
          element={
            <ProtectedRoute>
              <PremiumSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/boost"
          element={
            <ProtectedRoute>
              <Boost />
            </ProtectedRoute>
          }
        />
        <Route
          path="/boost/success"
          element={
            <ProtectedRoute>
              <BoostSuccess />
            </ProtectedRoute>
          }
        />
        <Route path="/promote/:videoId" element={<ProtectedRoute><Promote /></ProtectedRoute>} />
        <Route path="/promote/success" element={<ProtectedRoute><PromoteSuccess /></ProtectedRoute>} />
        <Route path="/gifts" element={<ProtectedRoute><Gifts /></ProtectedRoute>} />
        <Route path="/gifts/success" element={<ProtectedRoute><GiftsSuccess /></ProtectedRoute>} />
        <Route path="/live-rtc/:sessionId" element={<ProtectedRoute><LiveRTC /></ProtectedRoute>} />
        <Route path="/duet/:videoId" element={<ProtectedRoute><DuetRecorder /></ProtectedRoute>} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/plans/success" element={<ProtectedRoute><PlansSuccess /></ProtectedRoute>} />
        <Route path="/distribution" element={<ProtectedRoute><DistributionHub /></ProtectedRoute>} />
        <Route path="/ai-dating" element={<ProtectedRoute><AIDating /></ProtectedRoute>} />
        <Route path="/ai-dating/create" element={<ProtectedRoute><AIDatingCreate /></ProtectedRoute>} />
        <Route path="/ai-dating/chat/:characterId" element={<ProtectedRoute><AIDatingChat /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </WebSocketProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
